/**
 * HTTP adapter for IProgressRepository — syncs sessions/checkpoints to platform API (Phase B)
 */
import type {
  GameSessionRef,
  IProgressRepository,
  ProgressEventInput,
} from '../../domain/ports/IProgressRepository';
import { getPlatformApiUrl } from '../../infrastructure/config/platform';

export type FetchLike = (
  input: string | URL | Request,
  init?: RequestInit,
) => Promise<Response>;

interface CreateSessionResponse {
  id: string;
  tenantId: string;
}

interface SessionResponse {
  checkpoint: Record<string, unknown> | null;
}

export class HttpProgressRepository implements IProgressRepository {
  constructor(
    private readonly baseUrl: string = getPlatformApiUrl(),
    private readonly fetchFn: FetchLike = (...args) => fetch(...args),
  ) {}

  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  private async parseErrorDetail(response: Response): Promise<string> {
    try {
      const body = (await response.json()) as { error?: string };
      if (typeof body.error === 'string' && body.error.trim()) {
        return `: ${body.error}`;
      }
    } catch {
      // Response may not be JSON
    }
    return '';
  }

  private async requestJson<T>(
    path: string,
    init?: RequestInit,
    allowNotFound = false,
  ): Promise<T | null> {
    const response = await this.fetchFn(this.url(path), init);

    if (allowNotFound && response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const detail = await this.parseErrorDetail(response);
      const method = init?.method ?? 'GET';
      throw new Error(`Platform API ${method} ${path} failed (${response.status})${detail}`);
    }

    if (response.status === 204) {
      return null;
    }

    return (await response.json()) as T;
  }

  async createSession(industry?: string, playerRef?: string): Promise<GameSessionRef> {
    const body: Record<string, string> = {};
    if (industry) {
      body.industry = industry;
    }
    if (playerRef) {
      body.playerRef = playerRef;
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (playerRef) {
      headers['X-Player-Ref'] = playerRef;
    }

    const data = await this.requestJson<CreateSessionResponse>('/v1/sessions', {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!data?.id || !data?.tenantId) {
      throw new Error('Platform API POST /v1/sessions returned an invalid session payload');
    }

    return {
      sessionId: data.id,
      tenantId: data.tenantId,
    };
  }

  async saveCheckpoint(sessionId: string, checkpoint: Record<string, unknown>): Promise<void> {
    await this.requestJson(`/v1/sessions/${encodeURIComponent(sessionId)}/checkpoint`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(checkpoint),
    });
  }

  async loadCheckpoint(sessionId: string): Promise<Record<string, unknown> | null> {
    const data = await this.requestJson<SessionResponse>(
      `/v1/sessions/${encodeURIComponent(sessionId)}`,
      undefined,
      true,
    );

    if (!data) {
      return null;
    }

    return data.checkpoint ?? null;
  }

  async recordEvent(sessionId: string, event: ProgressEventInput): Promise<void> {
    await this.requestJson(`/v1/sessions/${encodeURIComponent(sessionId)}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventType: event.eventType,
        payload: event.payload,
      }),
    });
  }
}